/**
 * identifyCurrentSpeaker — sample a few seconds of mic audio and ask
 * the personalization API which enrolled household member is talking.
 *
 * Used by LinkLiveSession right after wake-word detection to pre-tag
 * the upcoming capture session with the right member_id, sparing the
 * server-side diarize sweep from having to re-attribute chunks.
 *
 * Returns one of:
 *   { memberId, displayName, confidence }   strong match found
 *   { unknown: true, reason }                no match (no enrolled
 *                                            members, low score,
 *                                            tie between candidates)
 */

import { createClient } from "@/lib/supabase/client";

const WARDROBE_URL = process.env.NEXT_PUBLIC_LBX_WARDROBE_URL
  || "http://localhost:8080";

const SAMPLE_RATE = 16_000;
const DEFAULT_DURATION_S = 3.5;

export interface IdentifyMatch {
  memberId: string;
  displayName: string;
  relationship?: string;
  confidence: number;
  unknown?: false;
}
export interface IdentifyUnknown {
  unknown: true;
  reason: string;
  bestScore?: number;
}
export type IdentifyResult = IdentifyMatch | IdentifyUnknown;

/**
 * Capture a short audio sample from the default mic and POST it to the
 * personalization identify-speaker endpoint. Total wall time ≈
 * `durationS + 200ms` for the network roundtrip.
 *
 * The caller must already have mic permission (no first-call prompt
 * handling here). LinkLiveSession's wake-word detector already holds
 * the mic open so this is a no-op grant in practice.
 */
export async function identifyCurrentSpeaker({
  householdId,
  durationS = DEFAULT_DURATION_S,
}: {
  householdId: string;
  durationS?: number;
}): Promise<IdentifyResult> {
  const supabase = createClient();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { unknown: true, reason: "not_signed_in" };

  // 1. Capture
  const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)({
    sampleRate: SAMPLE_RATE,
  });
  try {
    const src = ctx.createMediaStreamSource(mic);
    const proc = ctx.createScriptProcessor(1024, 1, 1);
    const bufs: Float32Array[] = [];
    proc.onaudioprocess = (ev) => {
      bufs.push(new Float32Array(ev.inputBuffer.getChannelData(0)));
    };
    src.connect(proc);
    proc.connect(ctx.destination);
    await new Promise((res) => setTimeout(res, durationS * 1000));
    proc.disconnect();

    // 2. Encode
    const total = bufs.reduce((s, b) => s + b.length, 0);
    const pcm = new Float32Array(total);
    let off = 0;
    for (const b of bufs) { pcm.set(b, off); off += b.length; }
    const wav = floatPcmToWav(pcm, SAMPLE_RATE);

    // 3. Identify
    const form = new FormData();
    form.set("audio", new Blob([wav], { type: "audio/wav" }), "probe.wav");
    const url = `${WARDROBE_URL}/api/v1/personalization/identify-speaker`
      + `?household_id=${encodeURIComponent(householdId)}`;
    const resp = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${session.access_token}` },
      body: form,
    });
    if (!resp.ok) {
      return { unknown: true, reason: `http_${resp.status}` };
    }
    const j = await resp.json();
    if (j.unknown) {
      return { unknown: true, reason: j.reason, bestScore: j.best_score };
    }
    return {
      memberId: j.member_id,
      displayName: j.display_name,
      relationship: j.relationship,
      confidence: j.confidence,
    };
  } finally {
    mic.getTracks().forEach((t) => t.stop());
    try { await ctx.close(); } catch (_) { /* noop */ }
  }
}

// Float32 PCM → 16-bit RIFF WAV (matches services/training/enrollment.py).
function floatPcmToWav(samples: Float32Array, rate: number): ArrayBuffer {
  const n = samples.length;
  const buf = new ArrayBuffer(44 + n * 2);
  const view = new DataView(buf);
  const ws = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  ws(0, "RIFF"); view.setUint32(4, 36 + n * 2, true); ws(8, "WAVE");
  ws(12, "fmt "); view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  ws(36, "data"); view.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}
